import React, { useRef } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import ReactToPrint from "react-to-print";
import { QRCodeSVG } from "qrcode.react";
import { ASSET_STICKER_LINK_BASE } from "../../../../constants";

// Sized for 2" x 4" labels, 10 per US Letter sheet (Avery 5163 / 8163
// layout). Plain paper works too: cut along the dashed outlines.
const LABELS_PER_PAGE = 10;
const LABEL_PRINT_CSS = `
  @page { size: letter portrait; margin: 0.5in 0.16in; }
  html, body { background: #fff !important; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .asset-label-page { break-after: page; page-break-after: always; }
  .asset-label-page:last-child { break-after: auto; page-break-after: auto; }
  .asset-label-page { box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
  .asset-label { border-color: transparent !important; }
`;

export function assetStickerLink(asset) {
  return `${ASSET_STICKER_LINK_BASE}${asset.qr_token}`;
}

function AssetLabel({ asset, storeName }) {
  return (
    <div
      className="asset-label flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3"
      style={{ width: "4in", height: "2in", boxSizing: "border-box", breakInside: "avoid" }}
    >
      <QRCodeSVG value={assetStickerLink(asset)} size={124} level="M" />
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-[28px] leading-none font-bold text-slate-900 tabular-nums">{asset.code}</span>
        <span
          className="text-[15px] leading-tight font-semibold text-slate-900 overflow-hidden"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
        >
          {asset.name}
        </span>
        <span className="text-[11px] text-slate-600 truncate">
          {[asset.location, storeName].filter(Boolean).join(" · ")}
        </span>
        <span className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-violet-700">
          Scan to report an issue
        </span>
      </div>
    </div>
  );
}

// Print preview and print for one or more assets' stickers.
function AssetLabelSheet({ open, onClose, assets, storeName }) {
  const sheetRef = useRef(null);
  const sorted = [...(assets || [])].sort(
    (a, b) =>
      (a.location || "").localeCompare(b.location || "") ||
      (a.code || "").localeCompare(b.code || "", undefined, { numeric: true })
  );
  const pages = [];
  for (let i = 0; i < sorted.length; i += LABELS_PER_PAGE) {
    pages.push(sorted.slice(i, i + LABELS_PER_PAGE));
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(900px, calc(100vw - 32px))",
          height: "90vh",
          bgcolor: "#f1f5f9",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-white rounded-t-xl border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">Print QR labels</h2>
            <p className="text-sm text-slate-500">
              {sorted.length} label{sorted.length === 1 ? "" : "s"}, grouped by area. Fits 2" x 4"
              label sheets (10 per page).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <ReactToPrint
              documentTitle="Asset labels"
              pageStyle={LABEL_PRINT_CSS}
              content={() => sheetRef.current}
              trigger={() => (
                <button
                  type="button"
                  disabled={sorted.length === 0}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium disabled:opacity-50"
                >
                  <PrintIcon fontSize="small" />
                  Print
                </button>
              )}
            />
            <button type="button" aria-label="Close" onClick={onClose} className="text-slate-500">
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <div ref={sheetRef} className="flex flex-col items-center gap-6">
            {pages.map((page, i) => (
              <div
                key={i}
                className="asset-label-page bg-white shadow-sm"
                style={{
                  width: "8.5in",
                  padding: "0.5in 0.16in",
                  boxSizing: "border-box",
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 4in)",
                  gridAutoRows: "2in",
                  columnGap: "0.18in",
                }}
              >
                {page.map((asset) => (
                  <AssetLabel key={asset.uuid} asset={asset} storeName={storeName} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default AssetLabelSheet;
